import React, { useEffect, useState, useMemo } from 'react';
import {
  Container, Select, MenuItem, TextField, Button, Table, TableHead, TableRow,
  TableCell, TableBody, Typography, Box,
  TableRowProps,
  styled,
  TableContainer,
  createTheme,
  ThemeProvider,
  SelectChangeEvent,
  Collapse,
  Paper,
  IconButton
} from '@mui/material';
import {
  startOfMonth, endOfMonth, format, parseISO, addMonths, subMonths
} from 'date-fns';
import NavBar from '../Navigation/Navbar';
import { KeyboardArrowDown, KeyboardArrowUp } from '@mui/icons-material';


const locations = [
  'Steeles', 'New-Scarborough', 'Brampton', 'Newmarket', 'Dufferin-Orfus',
  'Vaughan', 'Queensway', 'Niagara','Kingston', 'Alliston', 'OPM-Hamilton', 'Pickering',
  'Cartwright', 'Yorkgate',
];

// Custom theme
const theme = createTheme({
  typography: {
    fontFamily: 'Arial, sans-serif', // Set default font to Arial
  },
  components: {
    MuiTableCell: {
      styleOverrides: {
        root: ({ theme }) => ({
          padding: '4px 8px',
          whiteSpace: 'nowrap',
          border: '1px solid rgba(224, 224, 224, 1)', // Add borders to cells
          fontFamily: 'Arial, sans-serif', // Ensure Arial font
          textAlign: 'center', // Center-align text
          [theme.breakpoints.down('sm')]: {
            fontSize: '0.75rem',
            padding: '2px 4px',
          },
          [theme.breakpoints.up('sm')]: {
            fontSize: '0.875rem',
            padding: '4px 8px',
          },
        }),
        head: ({ theme }) => ({
          backgroundColor: '#f5f5f5',
          fontWeight: 'bold',
          border: '1px solid rgba(224, 224, 224, 1)',
          whiteSpace: 'nowrap',
          fontFamily: 'Arial, sans-serif', // Ensure Arial font
          textAlign: 'center', // Center-align header text
          [theme.breakpoints.down('sm')]: {
            fontSize: '0.75rem',
            padding: '2px 4px',
          },
          [theme.breakpoints.up('sm')]: {
            fontSize: '0.875rem',
            padding: '4px 8px',
          },
        }),
      },
    },
    MuiInputBase: {
      styleOverrides: {
        root: ({ theme }) => ({
          fontFamily: 'Arial, sans-serif', // Ensure Arial font
          textAlign: 'center', // Center-align input text
          [theme.breakpoints.down('sm')]: {
            fontSize: '0.75rem',
          },
          [theme.breakpoints.up('sm')]: {
            fontSize: '0.875rem',
          },
        }),
        input: {
          '&::-webkit-outer-spin-button, &::-webkit-inner-spin-button': {
            '-webkit-appearance': 'none',
            margin: 0,
          },
          '&[type=number]': {
            '-moz-appearance': 'textfield',
          },
        },
      },
    },
    MuiButton: {
      styleOverrides: {
        root: ({ theme }) => ({
          fontFamily: 'Arial, sans-serif', // Ensure Arial font
          textAlign: 'center', // Center-align button text
          [theme.breakpoints.down('sm')]: {
            fontSize: '0.75rem',
          },
          [theme.breakpoints.up('sm')]: {
            fontSize: '0.875rem',
          },
        }),
      },
    },
    MuiSelect: {
      styleOverrides: {
        select: ({ theme }) => ({
          fontFamily: 'Arial, sans-serif', // Ensure Arial font
          textAlign: 'center', // Center-align select text
          [theme.breakpoints.down('sm')]: {
            fontSize: '0.75rem',
          },
          [theme.breakpoints.up('sm')]: {
            fontSize: '0.875rem',
          },
        }),
      },
    },
    MuiInputLabel: {
      styleOverrides: {
        root: ({ theme }) => ({
          fontFamily: 'Arial, sans-serif', // Ensure Arial font
          textAlign: 'center', // Center-align labels
          [theme.breakpoints.down('sm')]: {
            fontSize: '0.75rem',
          },
          [theme.breakpoints.up('sm')]: {
            fontSize: '0.875rem',
          },
        }),
      },
    },
    MuiTypography: {
      styleOverrides: {
        root: {
          fontFamily: 'Arial, sans-serif', // Ensure Arial font
          textAlign: 'center', // Center-align typography
        },
      },
    },
  },
});

// Styled components
const Root = styled('div')({
  display: 'flex',
  flexDirection: 'column',
  height: '84vh', // Adjust height if necessary
  backgroundColor: '#f0f2f5',
});

const TableContainerStyled = styled(TableContainer)({
  flexGrow: 1,
  overflowX: 'auto',
  overflowY: 'auto',
});

const FiltersContainer = styled('div')({
  position: 'fixed',
  bottom: 0,
  left: 0,
  right: 0,
  backgroundColor: '#fff',
  padding: '0.3rem',
  borderTop: '1px solid #ccc',
});

const TableRowStyled = styled(TableRow)<TableRowProps>(({ theme }) => ({
  '&:nth-of-type(odd)': {
    backgroundColor: theme.palette.action.hover,
  },
  cursor: 'pointer', // Indicate the row is clickable
  '&:hover': {
    backgroundColor: '#ffffe0', // Light yellow on hover
  },
  '&.Mui-selected': {
    backgroundColor: '#ffffe0', // Light yellow when selected
  },
  '&.Mui-selected:hover': {
    backgroundColor: '#ffffe0', // Maintain light yellow on hover when selected
  },
}));



interface RegisterEntry {
   close_type: string;
    register: string;
    counted_amount: number;
  }
  
  interface ApiResponse {
    date: string;
    entries: RegisterEntry[];
  }
  
  interface RowData {
    date: string;
    registers: { [register: string]: number }; // <-- change from string to number
    details: RegisterEntry[];
  }

  interface RegisterDetailRow {
   tender_name: string;
    system_amount: number;
    counted_amount: number;
    register: string;
  }
  
  
const CloseRegisterView: React.FC = () => {
  const currentMonth = new Date();
  const [location, setLocation] = useState('');
  const [month, setMonth] = useState<string | null>(format(currentMonth, 'yyyy-MM'));
  const [startDate, setStartDate] = useState<string | null>(format(startOfMonth(currentMonth), 'yyyy-MM-dd'));
  const [endDate, setEndDate] = useState<string | null>(format(endOfMonth(currentMonth), 'yyyy-MM-dd'));
  const [detailsCache, setDetailsCache] = useState<{
    [key: string]: { [register: string]: RegisterDetailRow[] }
  }>({});
  
  const [data, setData] = useState<ApiResponse[]>([]);
  const [expandedRows, setExpandedRows] = useState<Set<string>>(new Set());


  const transformedData: RowData[] = useMemo(() => {
    return data.map((entry) => {
        const registers: { [register: string]: number } = {};
        entry.entries.forEach((e) => {
          if (e.close_type === 'Total') {
            registers[e.register] = e.counted_amount;
          }
        });
        
      return {
        date: entry.date,
   
        registers,
        details: entry.entries,
      };
    });
  }, [data]);

  const allRegisters = useMemo(() => {
    const registerSet = new Set<string>();
    transformedData.forEach((row) => {
      Object.keys(row.registers).forEach((register) => registerSet.add(register));
    });
    return Array.from(registerSet).sort();
  }, [transformedData]);



  const fetchData = async () => {
    if (!location) return;

    const res = await fetch('/data/getCloseRegisterSummary', {
      method: 'POST',
      credentials: 'include',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ location, startDate, endDate, month })
    });

    const result: ApiResponse[] = await res.json();
    setData(result);

  };



  useEffect(() => {
    fetchData();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [location, month, startDate, endDate]);

  const fetchRegisterDetails = async (date: string, register: string) => {
    const cacheKey = `${date}_${register}`;
    if (detailsCache[date]?.[register]) return; // Already fetched
  
    const res = await fetch('/data/getCloseRegisterDetails', {
      method: 'POST',
      credentials: 'include',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ location, date, register })
    });
  
    const result: RegisterDetailRow[] = await res.json();
    setDetailsCache(prev => ({
      ...prev,
      [date]: {
        ...(prev[date] || {}),
        [register]: result,
      },
    }));
  };

  const toggleRow = async (key: string, date: string) => {
    if (expandedRows.has(key)) {
      setExpandedRows(prev => {
        const newSet = new Set(prev);
        newSet.delete(key);
        return newSet;
      });
    } else {
      // Preload all register details for this row
      await Promise.all(
        allRegisters.map(register => fetchRegisterDetails(date, register))
      );
      setExpandedRows(prev => new Set(prev).add(key));
    }
  };

    const handleLocationChange = (event: SelectChangeEvent<string>) => {
      setLocation(event.target.value);
    };
  
    const handleMonthChange = (event: SelectChangeEvent<string>) => {
      const selectedMonth = event.target.value;
      setMonth(selectedMonth);
      setStartDate(null);
      setEndDate(null);
    };
  
    const handleStartDateChange = (date: string) => {
      setStartDate(date);
      setMonth(null);
    };
  
    const handleEndDateChange = (date: string) => {
      setEndDate(date);
      setMonth(null);
    };
    
  const generateMonthOptions = () => {
    const months = [];
    let date = subMonths(currentMonth, 11);
    for (let i = 0; i < 12; i++) {
      months.push({ label: format(date, 'MMMM yyyy'), value: format(date, 'yyyy-MM') });
      date = addMonths(date, 1);
    }
    return months;
  };

  const formatCurrency = (value: number | null | undefined) =>
    value !== null && value !== undefined
      ? `$${value.toLocaleString('en-US', { minimumFractionDigits: 2 })}`
      : '-';




  
  return (
  
<>
<NavBar />
      <ThemeProvider theme={theme}>
        <Root>
       <TableContainerStyled>
                  <Table
                    stickyHeader
                    style={{ tableLayout: 'auto', minWidth: 800 }}
                    size="small"
                  >
       
          <TableHead>
            <TableRow>
              <TableCell />
              <TableCell>Date</TableCell>
            
              {allRegisters.map((register) => (
  <TableCell key={register}>{register}</TableCell>
))}

<TableCell style={{ fontWeight: 600 }}>Total Sales</TableCell>

            </TableRow>
          </TableHead>
          <TableBody>
            {transformedData.map((row) => {
              const rowKey = `${row.date}-${row.date}`;
              return (
                <React.Fragment key={rowKey}>
                  <TableRowStyled>
                    <TableCell>
                    <IconButton size="small" onClick={() => toggleRow(rowKey, row.date)}>
                        {expandedRows.has(rowKey) ? <KeyboardArrowUp /> : <KeyboardArrowDown />}
                      </IconButton>
                    </TableCell>
                    <TableCell>{format(parseISO(row.date), 'MMM dd, yyyy')}</TableCell>
                    {allRegisters.map((register) => (
  <TableCell key={register} align="center">
    {row.registers[register] !== undefined
      ? formatCurrency(Number(row.registers[register])) // display formatted
      : '-'}
  </TableCell>
))}

{/* Total Sales column */}
<TableCell align="center" style={{ fontWeight: 600 }}>
  {formatCurrency(
    allRegisters.reduce((total, register) => {
      const val = row.registers[register];
      const num = Number(val);
      return !isNaN(num) ? total + num : total;
    }, 0)
  )}
</TableCell>






                  </TableRowStyled>
                  <TableRow>
  <TableCell style={{ paddingBottom: 0, paddingTop: 0 }} colSpan={allRegisters.length + 1}>
    <Collapse in={expandedRows.has(rowKey)} timeout="auto" unmountOnExit>
      <Box margin={1}>
        <Table size="small">
        
          <TableBody>
           <TableRow>
  <TableCell style={{ paddingBottom: 0, paddingTop: 0 }} colSpan={allRegisters.length + 2}>
    <Collapse in={expandedRows.has(rowKey)} timeout="auto" unmountOnExit>
      <Box margin={1}>
        <Table size="small">
          <TableBody>
            <TableRow>
              {allRegisters.map((register) => {
                const details = detailsCache[row.date]?.[register] || [];

                return details.length > 0 ? (
                  <TableCell key={register} align="center" sx={{ verticalAlign: 'top', padding: 0.5 }}>
                    <Table size="small" sx={{ minWidth: 240 }}>
                      <TableHead>
                        <TableRow>
                          <TableCell
                            colSpan={3}
                            style={{
                              fontWeight: 600,
                              fontSize: '0.75rem',
                              textAlign: 'center',
                              backgroundColor: '#f9f9f9',
                            }}
                          >
                            {register} Details
                          </TableCell>
                        </TableRow>
                        <TableRow>
                          <TableCell style={{ fontWeight: 600, fontSize: '0.75rem' }}>Tender</TableCell>
                          <TableCell style={{ fontWeight: 600, fontSize: '0.75rem' }}>System</TableCell>
                          <TableCell style={{ fontWeight: 600, fontSize: '0.75rem' }}>Counted</TableCell>
                        </TableRow>
                      </TableHead>
                      <TableBody>
                        {details.map((d, idx) => (
                          <TableRowStyled key={idx}>
                            <TableCell style={{ fontSize: '0.75rem' }}>{d.tender_name}</TableCell>
                            <TableCell style={{ fontSize: '0.75rem' }}>{formatCurrency(d.system_amount)}</TableCell>
                            <TableCell style={{ fontSize: '0.75rem' }}>{formatCurrency(d.counted_amount)}</TableCell>
                          </TableRowStyled>
                        ))}
                      </TableBody>
                    </Table>
                  </TableCell>
                ) : (
                  <TableCell key={register} />
                );
              })}
            </TableRow>
          </TableBody>
        </Table>
      </Box>
    </Collapse>
  </TableCell>
</TableRow>

          </TableBody>
        </Table>
      </Box>
    </Collapse>
  </TableCell>
</TableRow>


                </React.Fragment>
              );
            })}
          </TableBody>
        </Table>
        </TableContainerStyled>
        <FiltersContainer>
                  <Container>
                    <Box
                      sx={{
                        display: 'flex',
                        gap: '0.3rem',
                        alignItems: 'center',
                        justifyContent: 'space-between',
                        flexWrap: 'wrap',
                      }}
                    >
                      <Select
                        value={location}
                        onChange={handleLocationChange}
                        displayEmpty
                        variant="outlined"
                        size="small"
                        sx={{
                          minWidth: '127px',
                          fontSize: { xs: '0.75rem', sm: '0.875rem' },
                        }}
                      >
                        <MenuItem value="" disabled>
                          Select Location
                        </MenuItem>
                        {locations.map((loc) => (
                          <MenuItem key={loc} value={loc}>
                            {loc}
                          </MenuItem>
                        ))}
                      </Select>
      
                      <Select
                        value={month || ''}
                        onChange={handleMonthChange}
                        displayEmpty
                        variant="outlined"
                        size="small"
                        sx={{
                          minWidth: '130px',
                          fontSize: { xs: '0.75rem', sm: '0.875rem' },
                        }}
                      >
                        <MenuItem value="" disabled>
                          Select Month
                        </MenuItem>
                        {generateMonthOptions().map((option) => (
                          <MenuItem key={option.value} value={option.value}>
                            {option.label}
                          </MenuItem>
                        ))}
                      </Select>
      
                      <TextField
                        label="Start Date"
                        type="date"
                        value={startDate || ''}
                        onChange={(e) => handleStartDateChange(e.target.value)}
                        InputLabelProps={{
                          shrink: true,
                          sx: { fontSize: { xs: '0.75rem', sm: '0.875rem' } },
                        }}
                        variant="outlined"
                        size="small"
                        margin="dense"
                        sx={{ minWidth: '130px' }}
                        inputProps={{
                          style: { fontSize: '0.75rem' },
                        }}
                      />
      
                      <TextField
                        label="End Date"
                        type="date"
                        value={endDate || ''}
                        onChange={(e) => handleEndDateChange(e.target.value)}
                        InputLabelProps={{
                          shrink: true,
                          sx: { fontSize: { xs: '0.75rem', sm: '0.875rem' } },
                        }}
                        variant="outlined"
                        size="small"
                        margin="dense"
                        sx={{ minWidth: '130px' }}
                        inputProps={{
                          style: { fontSize: '0.75rem' },
                        }}
                      />
      
                      <Button
                        variant="contained"
                        onClick={fetchData}
                        size="small"
                        sx={{
                          fontSize: { xs: '0.75rem', sm: '0.875rem' },
                        }}
                      >
                        Get Data
                      </Button>
      
                     
                    </Box>
                  </Container>
                </FiltersContainer>
       </Root>
            </ThemeProvider>
      </>
    // </Container>
  );
};

export default CloseRegisterView;
